import type { ApiSchemas } from '@rental/contracts/client'
import type { StaffLocale } from '@rental/domain'
import { format, formatDate, localized, type StaffMessages } from '@rental/i18n'

type Refusal = ApiSchemas['Refusal']

/** What turns codes into names: the catalogue the booking screen already loaded. */
export interface Lookups {
  locations: readonly Pick<ApiSchemas['Location'], 'code' | 'name'>[]
  zones: readonly Pick<ApiSchemas['DeliveryZone'], 'code' | 'name'>[]
  extras: readonly Pick<ApiSchemas['Extra'], 'code' | 'name'>[]
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/** Why a rental cannot be booked, in staff words, with places, extras and dates by name. */
export function refusalText(
  refusal: Refusal,
  locale: StaffLocale,
  m: StaffMessages,
  lookups: Lookups,
): string {
  const params: Record<string, string | number> = {}
  for (const [key, value] of Object.entries(refusal.params)) {
    if (value === null) continue
    if (key === 'leg' && (value === 'pickup' || value === 'return')) {
      params[key] = m.legs[value]
    } else if (key === 'location') {
      params[key] = lookups.locations.find((place) => place.code === value)?.name ?? String(value)
    } else if (key === 'zone') {
      const zone = lookups.zones.find((place) => place.code === value)
      params[key] = zone ? localized(zone.name, locale) : String(value)
    } else if (key === 'extra') {
      const extra = lookups.extras.find((item) => item.code === value)
      params[key] = extra ? localized(extra.name, locale) : String(value)
    } else if (typeof value === 'string' && ISO_DATE.test(value)) {
      params[key] = formatDate(locale, value, 'short')
    } else {
      params[key] = value
    }
  }
  const code = refusal.code
  if (
    isOneDayRefusal(code) &&
    typeof refusal.params.from === 'string' &&
    refusal.params.from === refusal.params.to
  ) {
    // S2: "from Wed 16 Dec to Wed 16 Dec" reads as a mistake: one day is named once.
    return format(m.refusalsOneDay[code], { day: params.from ?? refusal.params.from })
  }
  return format(m.refusals[code], params)
}

/** The refusals that name a span of days ({from} to {to}). */
type OneDayRefusal = keyof StaffMessages['refusalsOneDay']
const isOneDayRefusal = (code: Refusal['code']): code is Refusal['code'] & OneDayRefusal =>
  code === 'season_not_configured' || code === 'day_price_not_configured' || code === 'stop_sale'
